import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { BeneListService } from '../../../services/beneList.service';
import { ValidatorService } from '../../../../../../angular-shared/services/validators-reactive.service';
import { FormStateService } from '../../../../../common/services/form-state.service';
import { Beneficiary } from '../../../interfaces/policy-admin-interfaces/beneficiary.interface';
import { Party } from '../../../interfaces/policy-admin-interfaces/party.interface';
import { ModalRouteService } from '../../../services/modal-route.service';
import { Utils } from '../../../../../Utils';

@Component({
  selector: 'bene-form-other-trust',
  templateUrl: './bene-form.other-trust.component.html',
})
export class BeneFormOtherTrustComponent {
  beneForm: FormGroup;
  protected bene: Beneficiary;
  protected beneRelationship: string;
  protected type: string;
  protected percent: number;

  protected relationship: string;
  protected edit: boolean = false;

  mask = [/^([0-1])/, /\d/, '/', /^(0|1|2|3)/, /\d/, '/', /^(1|2)/, /^(9|0)/, /\d/, /\d/];

  public taxIdOptions = [
    { value: 'itin', description: 'TIN' },
    { value: 'ssn', description: 'SSN' },
  ];
  public trustTypeOptions = [
    { value: 'revocable', description: 'Revocable' },
    { value: 'irrevocable', description: 'Irrevocable' },
  ];
  public stateList = Utils.getStateList();

  public widgetType: string;
  cityList: string[];
  isZipLoading: boolean = false;

  constructor(
    protected beneListService: BeneListService,
    private modalRouteService: ModalRouteService,
    private router: Router,
    private activatedRouter: ActivatedRoute,
    private fb: FormBuilder
  ) {
    this.widgetType = this.beneListService.getChannelName();

    this.bene = this.beneListService.getCurrentBene() as Beneficiary;
    this.type = this.beneListService.getPrimaryOrContingent();
    this.percent = this.bene.ratio;

    this.beneRelationship = this.bene.beneficiaryType;

    if (this.bene.partyInfo.name !== '') {
      this.edit = true;
    }
    let trustDate = '';
    if ((this.bene.partyInfo as Party).entity.formedDate) {
      trustDate = this.getDate();
    }
    const legalAddress = (this.bene.partyInfo as Party).entity.legalAddress;

    this.beneForm = this.fb.group({
      name: [this.bene.partyInfo.name, Validators.required],
      street: [FormStateService.cleanAddressString(legalAddress.street, true)],
      line1: [FormStateService.cleanAddressString(legalAddress.line1, true)],
      line2: [FormStateService.cleanAddressString(legalAddress.line2, true)],
      city: [FormStateService.cleanAddressString(legalAddress.city, true)],
      state: [FormStateService.cleanAddressString(legalAddress.state, true)],
      zip: [FormStateService.cleanAddressString(legalAddress.zip, true)],
      phone: [Utils.formatPhoneEdit(this.bene.partyInfo.phone.number), ValidatorService.phoneValidator],
      trustDate: [trustDate, [Validators.required, ValidatorService.pastDateValidator]],
      ssnTin: [(this.bene.partyInfo as Party).entity.govtIdType],
      govtId: [
        Utils.formatGovtIdEdit(
          (this.bene.partyInfo as Party).entity.govtId,
          (this.bene.partyInfo as Party).entity.govtIdType
        ),
        ValidatorService.govtIdValidator,
      ],
      trustType: [(this.bene.partyInfo as Party).entity.trustType],
      email: [this.bene.partyInfo.email, ValidatorService.emailValidator],
    });
  }

  saveBene(): void {
    const id = this.bene.id;
    this.bene = {
      ratio: this.percent,
      action: 'created',
      actionDate: this.bene.actionDate,
      beneficiaryType: this.beneRelationship,
      beneficiaryPartyType: 'trust',
      id: id,
      partyInfo: {
        name: this.beneForm.get('name').value,
        phone: {
          number: this.beneForm.get('phone').value,
        },
        email: this.beneForm.get('email').value,
        entity: {
          govtId: this.beneForm.get('govtId').value,
          formedDate: this.beneForm.get('trustDate').value,
          govtIdType: this.beneForm.get('ssnTin').value || 'other',
          trust: true,
          trustType: this.beneForm.get('trustType').value,
          legalAddress: {
            //Blank values must be changed to a single space so PA does not ignore the blank value and skip the save
            street: FormStateService.cleanAddressString(this.beneForm.get('street').value),
            line1: FormStateService.cleanAddressString(this.beneForm.get('line1').value),
            line2: FormStateService.cleanAddressString(this.beneForm.get('line2').value),
            city: FormStateService.cleanAddressString(this.beneForm.get('city').value),
            state: FormStateService.cleanAddressString(this.beneForm.get('state').value),
            zip: FormStateService.cleanAddressString(this.beneForm.get('zip').value),
          },
        },
      },
    };

    if (this.edit) {
      this.bene.action = 'modified';
      this.beneListService.updateBene(this.bene, this.type);
    } else {
      this.beneListService.addBeneList(this.bene, this.type);
    }

    this.modalRouteService.changeRoute('bene-list');
  }

  getDate(): string {
    const formedDate = (this.bene.partyInfo as Party).entity.formedDate;
    if (formedDate.toString().indexOf('T') !== -1) {
      return formedDate.toString().substring(0, formedDate.toString().indexOf('T'));
    } else {
      return formedDate;
    }
  }

  formatPhone(): void {
    if (this.beneForm.get('phone')) {
      const phoneNum = this.beneForm.get('phone').value;
      const numbers = phoneNum.replace(/\D/g, '');
      if (numbers.length === 10) {
        (<HTMLInputElement>document.getElementById('phone')).value =
          '(' + numbers.substring(0, 3) + ') ' + numbers.substring(3, 6) + '-' + numbers.substring(6);
      } else if (numbers.length > 10) {
        (<HTMLInputElement>document.getElementById('phone')).value =
          '(' +
          numbers.substring(0, 3) +
          ') ' +
          numbers.substring(3, 6) +
          '-' +
          numbers.substring(6, 10) +
          ' x' +
          numbers.substring(10);
      }
    }
  }

  formatGovtId(): void {
    if (this.beneForm.get('govtId')) {
      const idNum = this.beneForm.get('govtId').value;
      const numbers = idNum.replace(/\D/g, '');
      if (this.beneForm.get('ssnTin').value === 'itin') {
        (<HTMLInputElement>document.getElementById('govtId')).value =
          numbers.substring(0, 2) + '-' + numbers.substring(2);
      } else if (this.beneForm.get('ssnTin').value === 'ssn') {
        (<HTMLInputElement>document.getElementById('govtId')).value =
          numbers.substring(0, 3) + '-' + numbers.substring(3, 5) + '-' + numbers.substring(5);
      }
    }
  }

  isDisabled(): boolean {
    //invalid in edit or add
    if (this.beneForm.invalid || this.isZipLoading) {
      return true;
    }

    if (
      ValidatorService.pastDateValidator(this.beneForm.controls.trustDate as FormControl) ||
      ValidatorService.govtIdValidator(this.beneForm.controls.govtId as FormControl) ||
      ValidatorService.phoneValidator(this.beneForm.controls.phone as FormControl) ||
      ValidatorService.zipValidator(this.beneForm.controls.zip as FormControl) ||
      ValidatorService.emailValidator(this.beneForm.controls.email as FormControl)
    ) {
      return true;
    }

    //check to see that all addresses are inputted, or none are
    if (
      this.beneForm.get('street').value !== '' ||
      this.beneForm.get('line1').value !== '' ||
      this.beneForm.get('line2').value !== '' ||
      this.beneForm.get('city').value !== '' ||
      this.beneForm.get('state').value !== '' ||
      this.beneForm.get('zip').value !== ''
    ) {
      //if one of these is filled out, all must be filled out
      if (
        !(
          this.beneForm.get('street').value !== '' &&
          this.beneForm.get('city').value !== '' &&
          this.beneForm.get('state').value !== '' &&
          this.beneForm.get('zip').value !== ''
        )
      ) {
        return true;
      }
    }

    //different cases to validate if edit or if add
    if (this.edit) {
      // only if things have changed

      if (
        this.bene.partyInfo.name !== this.beneForm.get('name').value ||
        (this.bene.partyInfo as Party).entity.legalAddress.street !== this.beneForm.get('street').value ||
        (this.bene.partyInfo as Party).entity.legalAddress.line1 !== this.beneForm.get('line1').value ||
        (this.bene.partyInfo as Party).entity.legalAddress.line2 !== this.beneForm.get('line2').value ||
        (this.bene.partyInfo as Party).entity.legalAddress.city !== this.beneForm.get('city').value ||
        (this.bene.partyInfo as Party).entity.legalAddress.state !== this.beneForm.get('state').value ||
        (this.bene.partyInfo as Party).entity.legalAddress.zip !== this.beneForm.get('zip').value ||
        this.getDate() !== this.beneForm.get('trustDate').value ||
        Utils.formatGovtIdEdit(
          (this.bene.partyInfo as Party).entity.govtId,
          (this.bene.partyInfo as Party).entity.govtIdType
        ) !==
          Utils.formatGovtIdEdit(this.beneForm.get('govtId').value, (this.bene.partyInfo as Party).entity.govtIdType) ||
        Utils.formatPhoneEdit(this.bene.partyInfo.phone.number) !==
          Utils.formatPhoneEdit(this.beneForm.get('phone').value) ||
        (this.bene.partyInfo as Party).entity.trustType !== this.beneForm.get('trustType').value ||
        this.bene.partyInfo.email !== this.beneForm.get('email').value
      ) {
        return false;
      }
    } else {
      //only if things exist
      if (
        this.beneForm.get('name').value !== '' &&
        this.beneForm.get('trustDate').value !== '' &&
        this.beneForm.get('govtId').value !== ''
      ) {
        return false;
      }
    }
    return true;
  }

  resetAddress(): void {
    FormStateService.resetAddress(this.beneForm);
  }

  cancel(): void {
    this.modalRouteService.changeRoute('bene-list');
  }
}
